import {ControlValueAccessor} from "@angular/forms";
import {
    computed,
    Directive,
    ElementRef,
    input,
    Input,
    InputSignal,
    OnDestroy,
    output,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {FieldComponent} from "./field.component";

@Directive()
export abstract class AbstractField implements ControlValueAccessor, OnDestroy {

    public readonly inputId: InputSignal<string> = input<string>();
    public readonly label: InputSignal<string> = input<string>();
    public readonly link: InputSignal<string> = input<string>();
    public readonly placeholder: InputSignal<string> = input<string>();
    public readonly message: InputSignal<string> = input<string>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>({});
    public readonly readOnly: InputSignal<boolean> = input<boolean>(false);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);

    public readonly onActivateLink = output<void>();

    // noinspection Annotator
    protected readonly inputER: Signal<ElementRef> = viewChild("input");

    private readonly fieldComponent: Signal<FieldComponent> = viewChild(FieldComponent);

    /**
     * Exposed so subclass templates can wire the projected native control up to its woa-field's label/messages
     * (aria-labelledby/aria-describedby/aria-invalid) without each subclass duplicating the id-plumbing.
     */
    public readonly labelId: Signal<string | undefined> = computed(() => this.fieldComponent()?.labelId);
    public readonly describedBy: Signal<string | undefined> = computed(() => this.fieldComponent()?.describedBy());
    public readonly ariaInvalid: Signal<boolean> = computed(() => this.fieldComponent()?.hasError() ?? false);

    /**
     * Internal value signal
     */
    protected readonly _value: WritableSignal<any> = signal("");

    /**
     * Expose a read-only signal for templates & subclasses.
     * Subclasses should NEVER mutate this directly.
     */
    public readonly valueSignal = this._value.asReadonly();

    public readonly displayValue: Signal<any> = computed(() => this.valueSignal() ?? "");

    /**
     * ControlValueAccessor callbacks
     */
    private onChange: (value: any) => void = () => {
    };
    private onTouched: () => void = () => {
    };

    private destroyed = false;

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    /**
     * Compatibility bridge - ngModel requires this
     */
    @Input()
    set value(value: any) {
        this.setFromOutside(value);
    }

    get value(): any {
        return this._value();
    }

    protected handleInputEvent(event: Event): void {
        const target = event.target as HTMLInputElement | null;
        if (!target) {
            return;
        }
        this.setFromInside(target.value);
    }

    /**
     * Public API for subclasses.
     * Call this when the user changes the value
     * (e.g. input, select, toggle, etc.).
     */
    protected setFromInside(value: any): void {
        value = value ?? "";
        if (Object.is(value, this._value())) {
            return;
        }
        this._value.set(value);
        this.onChange(value);
    }

    /**
     * Called by Angular Forms OR input binding.
     */
    protected setFromOutside(value: any): void {
        value = value ?? "";
        if (Object.is(value, this._value())) {
            return;
        }
        this._value.set(value);
    }

    /**
     * ControlValueAccessor
     */

    writeValue(value: any): void {
        if (this.destroyed) {
            return;
        }
        value = value ?? "";
        this._value.set(value);
        this.onChange(value);
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
}

